import { useEffect, useRef, useState } from "react";
import "./game3.css";

export default function Game3() {
  const canvasRef = useRef(null);
  const [playerLane, setPlayerLane] = useState(1);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [enemies, setEnemies] = useState([]);
  const [powerups, setPowerups] = useState([]);
  const [speed, setSpeed] = useState(5);
  const [boostActive, setBoostActive] = useState(false);
  const [invincible, setInvincible] = useState(false);

  const lanesX = [50, 130, 210];
  const canvasWidth = 300;
  const canvasHeight = 480;

  const animationRef = useRef(null);
  const imagesLoadedRef = useRef(false);

  const playerImg = useRef(new Image());
  const enemyImg = useRef(new Image());
  const powerupImg = useRef(new Image());

  const particles = useRef([]);

  // Load images
  useEffect(() => {
    let loadedCount = 0;
    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === 3) imagesLoadedRef.current = true;
    };

    playerImg.current.src = "/player.png";
    playerImg.current.onload = checkAllLoaded;
    enemyImg.current.src = "/enemy.png";
    enemyImg.current.onload = checkAllLoaded;
    powerupImg.current.src = "/powerup.png";
    powerupImg.current.onload = checkAllLoaded;
  }, []);

  // Controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameOver) return;
      if (e.key === "ArrowLeft") moveLeft();
      if (e.key === "ArrowRight") moveRight();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gameOver]);

  const moveLeft = () => setPlayerLane((l) => Math.max(0, l - 1));
  const moveRight = () => setPlayerLane((l) => Math.min(2, l + 1));

  // Generate enemies
  useEffect(() => {
    if (gameOver) return;
    const interval = setInterval(() => {
      const lane = Math.floor(Math.random() * 3);
      const size = Math.random() > 0.7 ? 1.2 : 1;
      setEnemies((prev) => [...prev, { lane, y: -60, size }]);
    }, 1200);
    return () => clearInterval(interval);
  }, [gameOver]);

  // Generate power-ups
  useEffect(() => {
    if (gameOver) return;
    const interval = setInterval(() => {
      const lane = Math.floor(Math.random() * 3);
      setPowerups((prev) => [...prev, { lane, y: -60 }]);
    }, 5000);
    return () => clearInterval(interval);
  }, [gameOver]);

  // Update positions
  useEffect(() => {
    if (gameOver) return;
    const interval = setInterval(() => {
      const currentSpeed = boostActive ? speed * 1.5 : speed;

      setEnemies((prev) =>
        prev
          .map((e) => ({ ...e, y: e.y + currentSpeed }))
          .filter((e) => e.y < canvasHeight)
      );

      setPowerups((prev) =>
        prev
          .map((p) => ({ ...p, y: p.y + currentSpeed }))
          .filter((p) => p.y < canvasHeight)
      );

      setScore((s) => s + 1);
      if (score % 100 === 0) setSpeed((s) => s + 0.2);
    }, 50);
    return () => clearInterval(interval);
  }, [speed, gameOver, score, boostActive]);

  // Draw and animate
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let offsetY = 0;

    const drawParticles = () => {
      particles.current.forEach((p, i) => {
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
        p.x += p.vx;
        p.y += p.vy;
        p.alpha -= 0.02;
        if (p.alpha <= 0) particles.current.splice(i, 1);
      });
    };

    const draw = () => {
      if (!imagesLoadedRef.current) {
        animationRef.current = requestAnimationFrame(draw);
        return;
      }

      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      // Background pseudo-perspective
      ctx.fillStyle = "#555";
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // Drum cu benzile pseudo-perspective
      ctx.fillStyle = "#333";
      ctx.beginPath();
      ctx.moveTo(40, canvasHeight);
      ctx.lineTo(70, 0);
      ctx.lineTo(230, 0);
      ctx.lineTo(260, canvasHeight);
      ctx.closePath();
      ctx.fill();

      // Linii drum animate
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 4;
      ctx.setLineDash([20, 20]);
      offsetY = (offsetY + speed) % 40;
      for (let i = 0; i < 3; i++) {
        const startX = 40 + i * 60;
        const endX = 70 + i * 60;
        for (let y = -40 + offsetY; y < canvasHeight; y += 40) {
          ctx.beginPath();
          ctx.moveTo(startX, y);
          ctx.lineTo(endX, y + 20);
          ctx.stroke();
        }
      }
      ctx.setLineDash([]);

      // Player
      ctx.save();
      if (boostActive) ctx.shadowBlur = 20;
      if (invincible) ctx.shadowColor = "yellow";
      ctx.drawImage(playerImg.current, lanesX[playerLane] - 20, canvasHeight - 80, 40, 60);
      ctx.restore();

      // Enemies
      enemies.forEach((e) => {
        const w = 40 * e.size;
        const h = 60 * e.size;
        ctx.drawImage(enemyImg.current, lanesX[e.lane] - w / 2, e.y, w, h);

        if (!invincible && e.lane === playerLane && e.y + h > canvasHeight - 80 && e.y < canvasHeight - 20) {
          // Particles on collision
          for (let i = 0; i < 20; i++) {
            particles.current.push({
              x: lanesX[playerLane],
              y: canvasHeight - 50,
              vx: Math.random() * 4 - 2,
              vy: Math.random() * -4,
              size: Math.random() * 3 + 2,
              alpha: 1,
              color: "red",
            });
          }
          setGameOver(true);
        }
      });

      // Power-ups
      powerups.forEach((p, i) => {
        // Glow effect
        ctx.save();
        ctx.shadowBlur = 15;
        ctx.shadowColor = "cyan";
        ctx.drawImage(powerupImg.current, lanesX[p.lane] - 20, p.y, 40, 40);
        ctx.restore();

        if (p.lane === playerLane && p.y + 40 > canvasHeight - 80 && p.y < canvasHeight - 20) {
          // Particle effect
          for (let j = 0; j < 10; j++) {
            particles.current.push({
              x: lanesX[playerLane],
              y: canvasHeight - 50,
              vx: Math.random() * 4 - 2,
              vy: Math.random() * -4,
              size: Math.random() * 3 + 2,
              alpha: 1,
              color: "cyan",
            });
          }

          setPowerups((prev) => prev.filter((_, idx) => idx !== i));
          setScore((s) => s + 50);

          // Random power-up effect
          const effect = Math.random() > 0.5 ? "boost" : "invincible";
          if (effect === "boost") {
            setBoostActive(true);
            setTimeout(() => setBoostActive(false), 3000);
          } else {
            setInvincible(true);
            setTimeout(() => setInvincible(false), 3000);
          }
        }
      });

      drawParticles();

      if (!gameOver) animationRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animationRef.current);
  }, [playerLane, enemies, powerups, gameOver, speed, boostActive, invincible]);

  const resetGame = () => {
    setPlayerLane(1);
    setEnemies([]);
    setPowerups([]);
    setScore(0);
    setSpeed(5);
    setBoostActive(false);
    setInvincible(false);
    setGameOver(false);
    particles.current = [];
    animationRef.current = requestAnimationFrame(() => {});
  };

  return (
    <div className="app">
      <h1>Arcade Road Racer 🚗💨 V3 - CV</h1>
      <p>Score: {score}</p>
      <canvas ref={canvasRef} width={canvasWidth} height={canvasHeight} className="game-canvas" />
      {gameOver && (
        <div className="game-over">
          <h2>Game Over 😢</h2>
          <button onClick={resetGame}>Joacă din nou</button>
        </div>
      )}
      {/* Mobile Controls */}
      <div className="mobile-controls">
        <button onClick={moveLeft}>⬅️</button>
        <button onClick={moveRight}>➡️</button>
      </div>
      <p>Folosește săgețile sau butoanele mobile pentru a schimba banda. Colectează power-up-uri!</p>
    </div>
  );
}
